import type { SelfContainedCommand, CommandExecutionContext } from 'types/commands';
import type { StandardToolResult } from 'types/standardToolResults';
import { createSuccessResult, createErrorResult } from 'types/standardToolResults';
import { createFileLogger } from '../../../../utils/logger';
import { entityCreationRegistry } from '../../../../services/entityCreationRegistry';

const log = createFileLogger('MEMORY_CREATE');

export const memoryCreateCommand: SelfContainedCommand = {
  id: 'memory_create',
  description: '🧠 CREATE: Create a new AI-writable memory/knowledge entry.',
  autonomyLevel: 'auto',
  visualTreatment: 'demonstrative',

  schema: {
    parameters: [
      {
        name: 'name',
        type: 'string',
        description: 'Display name for the memory',
        required: false,
        defaultValue: 'New Memory',
      },
      {
        name: 'spaceId',
        type: 'string',
        description: 'Space to create the memory in',
        required: false,
      },
      {
        name: 'folderId',
        type: 'string',
        description: 'Folder to place the memory in',
        required: false,
      },
      {
        name: 'createWindow',
        type: 'boolean',
        description: 'Whether to open the memory editor after creation. Default true.',
        required: false,
        defaultValue: true,
      },
    ],
  },

  examples: {
    good: [
      {
        input: { name: 'User Preferences' },
        expectedOutput: createSuccessResult('Memory created', { entityId: 'memory-123' }),
        description: 'Create a new memory entry',
      },
    ],
    bad: [],
  },

  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const {
      name = 'New Memory',
      spaceId: providedSpaceId,
      folderId,
      createWindow = true,
    } = params;

    const spaceId = providedSpaceId || context.spaceId;

    try {
      log.log('[memory_create] Creating memory:', { name, spaceId });

      const entityStoreModule = await import('../../../../stores/entityStore');
      const entityStore = entityStoreModule.default.getState();

      const entity = await entityStore.actions.createEntity({
        entityType: 'Memory',
        name: name.trim(),
        spaceId,
        skeleton: {
          content: '',
          tags: [],
          source: 'user',
          groupIds: folderId ? [folderId] : [],
        },
      });

      const entityId = entity._id || entity.id;

      if (entityId && spaceId) {
        try {
          const parentId = folderId || spaceId;
          const { default: useDataspaceTreeStore } = await import('../../../../stores/dataspaceTreeStore');
          const treeStore = useDataspaceTreeStore.getState();
          treeStore._applyAddNode(`${parentId}:${entityId}`, {
            id: entityId,
            parentId,
            pos: 'M',
            expanded: false,
            kind: 'canonical',
          });
        } catch (treeErr) {
          log.warn('[memory_create] Tree registration failed (non-fatal):', treeErr);
        }
      }

      if (createWindow !== false && entityId) {
        try {
          const { useCommandRegistryStore } = await import('../../../../stores/commandRegistryStore');
          const { executeCommand } = useCommandRegistryStore.getState();
          await executeCommand('space_open_window', { entityId });
        } catch (windowErr) {
          log.warn('[memory_create] Failed to open window (non-fatal):', windowErr);
        }
      }

      log.log('[memory_create] Memory created:', entityId);

      return createSuccessResult(`Memory created: "${name.trim()}"`, {
        entityId,
        name: name.trim(),
      });
    } catch (err: any) {
      log.error('[memory_create] Failed:', err);
      return createErrorResult('Failed to create memory', err.message || 'Unknown error', 'execution');
    }
  },
};

entityCreationRegistry.register({
  commandId: 'memory_create',
  entityType: 'Memory',
  displayName: 'Memory',
  icon: {
    type: 'phosphor',
    phosphorIcon: 'Brain',
    phosphorWeight: 'duotone',
  },
  description: 'Create an AI-writable memory entry',
  canCreateInFolder: true,
  openAfterCreate: true,
  category: 'system',
  sortOrder: 21,
});
